import { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useController } from 'react-hook-form';

import { profileApi } from '@pukka/api';
import { Button, ScrollArea, Dialog, Checkbox, Input } from '@pukka/ui-components';
import { matchSorter } from 'match-sorter';

import { DiscountCreateFormSchemaType } from './discountFormSchema';
import { getOutletsFromProfile } from './utils';

interface DiscountOutletModalProps {
  readonly open: boolean;
  readonly control: Control<DiscountCreateFormSchemaType>;
  readonly defaultOutletIds: number[];
  readonly onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
}

function DiscountOutletModal({ open, onOpenChange, control, defaultOutletIds }: DiscountOutletModalProps) {
  const [selectedOutlets, setSelectedOutlets] = useState<Set<number>>(new Set(defaultOutletIds));
  const { data: profile } = profileApi.useProfile();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    field: { onChange },
  } = useController({
    control,
    name: 'outlet_ids',
  });

  const outletMatches = useMemo(() => {
    if (open) {
      return matchSorter(getOutletsFromProfile(profile), searchTerm, { keys: ['name'] });
    }

    return [];
  }, [profile, searchTerm, open]);

  const submitSelectedOutlets = useCallback(() => {
    onChange(Array.from(selectedOutlets).sort());
    onOpenChange(false);
  }, [onChange, onOpenChange, selectedOutlets]);

  const selectAll = useCallback(() => {
    const allOutletIds = getOutletsFromProfile(profile)
      .map((outlet) => Number(outlet.id))
      .sort();

    setSelectedOutlets(new Set(allOutletIds));
  }, [profile]);

  const onCancel = useCallback(() => {
    setSelectedOutlets(new Set(defaultOutletIds));
    onOpenChange(false);
  }, [defaultOutletIds, onOpenChange]);

  useEffect(() => {
    if (open) {
      setSelectedOutlets(new Set(defaultOutletIds));
    }
  }, [defaultOutletIds, open]);

  return (
    <Dialog open={open}>
      <Dialog.Overlay>
        <Dialog.Content className="max-w-xl overflow-auto">
          <div className="flex justify-center bg-blue-400 p-3.5 text-lg text-white">Assign Discount to Outlet</div>
          <div className="p-6">
            <Input
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
              }}
              role="searchbox"
              placeholder="Search"
              className="w-full text-xs"
            />
            <div className="my-4 flex items-center justify-between">
              <span className="text-neutral-700">Select Outlet</span>
              <button className="text-blue-400" type="button" onClick={selectAll}>
                Select All
              </button>
            </div>

            <ScrollArea className="max-h-60 px-2">
              <ul className="flex flex-col text-sm text-neutral-500">
                {outletMatches.map((outlet) => (
                  <li className="flex items-center justify-between pr-3 hover:bg-neutral-100" key={outlet.id}>
                    <label className="w-full px-2 py-1.5" htmlFor={String(outlet.id)}>
                      {outlet.name}
                    </label>
                    <Checkbox
                      role="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectedOutlets.add(Number(outlet.id));
                        } else {
                          selectedOutlets.delete(Number(outlet.id));
                        }

                        // need to always have these items sorted to persist the dirty state
                        setSelectedOutlets(new Set(selectedOutlets));
                      }}
                      id={String(outlet.id)}
                      value={outlet.id}
                      checked={selectedOutlets.has(Number(outlet.id))}
                    />
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </div>
          <div className="flex items-center justify-end gap-x-2 border-t border-neutral-200 p-3.5">
            <span data-testid="selected-outlet" className="mr-auto">
              Selected Outlet: {selectedOutlets.size}
            </span>
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={submitSelectedOutlets} disabled={selectedOutlets.size === 0}>
              Assign Discount
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export default DiscountOutletModal;
